import React, { useEffect, useMemo, useState } from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoLogoWhite from "../../../img/eco/logo-white.png";
import {
  ACTION_LABELS,
  actionOf,
  buildRoster,
  DEFAULT_PARAMS,
  EMPTY_INPUT,
  hourMan,
  round1,
  SimAction,
  SimParams,
  StoreInput,
} from "../simulator.rules";
import { getSimulatorStoresApi, SimulatorStore } from "../simulatorApi";
import "./RosterSimulator.css";

type Tab = "calculadora" | "escala" | "parametros";
type Saved = { inputs: Record<string, StoreInput>; params: SimParams };

// O que a pessoa digitou fica só neste navegador (conveniência para recarregar a página).
// Pode faltar (aba anônima, armazenamento bloqueado) e a tela funciona sem.
const STORAGE_KEY = "simuladorEscala:v1";
const load = (): Saved => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<Saved>;
      return { inputs: saved.inputs ?? {}, params: { ...DEFAULT_PARAMS, ...(saved.params ?? {}) } };
    }
  } catch {
    /* sem armazenamento: começa do zero */
  }
  return { inputs: {}, params: DEFAULT_PARAMS };
};
const persist = (saved: Saved) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch {
    /* sem armazenamento: segue só na memória */
  }
};

const ACTION_TONE: Record<SimAction, string> = {
  DEMISSAO: "bad",
  MANTER: "good",
  CONTRATAR_FREELANCER: "warn",
  CONTRATAR_QLP: "info",
};

const PARAM_FIELDS: Array<{ key: keyof SimParams; label: string; hint: string; step?: number }> = [
  { key: "daysInMonth", label: "Dias do mês", hint: "Dias em que a loja abre no mês." },
  { key: "shiftsPerDay", label: "Turnos por dia", hint: "O horário da loja é dividido igualmente entre os turnos (manhã e noite)." },
  { key: "breakHours", label: "Intervalo por turno (horas)", hint: "Descontado de cada turno: horas de trabalho = horas abertas ÷ turnos − intervalo.", step: 0.25 },
  { key: "qlpWorkDaysPerWeek", label: "Dias de trabalho do QLP por semana", hint: "6 = escala 6x1; 5 = escala 5x2. Dias trabalhados no mês = dias do mês × este valor ÷ 7." },
  { key: "freelancerShiftsPerMonth", label: "Diárias por freelancer no mês", hint: "Cada freelancer faz este número de diárias no mês, de um turno cada. Conta só na hora/homem." },
  { key: "fireBelow", label: "Abaixo deste valor: Demissão", hint: "Hora/homem abaixo deste valor: sobra gente na loja.", step: 0.1 },
  { key: "freelancerFrom", label: "A partir deste valor: Contratar freelancer", hint: "Entre o limite da demissão e este valor: Manter.", step: 0.1 },
  { key: "qlpFrom", label: "A partir deste valor: Contratar QLP", hint: "Entre o limite do freelancer e este valor: Contratar freelancer.", step: 0.1 },
];

const WEEKDAY_LETTERS = ["D", "S", "T", "Q", "Q", "S", "S"];
const fmt1 = (n: number) => round1(n).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Meses do seletor: três para trás e doze para a frente, a partir do mês atual. */
const monthOptions = () => {
  const now = new Date();
  return Array.from({ length: 16 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1);
    const label = d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    return { value: `${d.getFullYear()}-${d.getMonth() + 1}`, label: label.charAt(0).toUpperCase() + label.slice(1) };
  });
};

/** "" vira vazio; o resto, inteiro ≥ 0. */
const parseCount = (raw: string): number | null => {
  if (raw.trim() === "") return null;
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export default function RosterSimulatorPage() {
  const [stores, setStores] = useState<SimulatorStore[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("calculadora");
  const [saved, setSaved] = useState<Saved>(load);
  const [search, setSearch] = useState("");
  const [rosterStoreId, setRosterStoreId] = useState<string>("");
  const months = useMemo(monthOptions, []);
  const [rosterMonth, setRosterMonth] = useState(months[3].value);
  const [confirmClear, setConfirmClear] = useState(false);

  const { inputs, params } = saved;

  useEffect(() => {
    getSimulatorStoresApi()
      .then((list) => {
        setStores(list);
        setRosterStoreId((cur) => cur || list[0]?.id || "");
      })
      .catch((e: Error) => setLoadError(e.message));
  }, []);

  useEffect(() => persist(saved), [saved]);

  const inputOf = (id: string): StoreInput => inputs[id] ?? EMPTY_INPUT;
  const setInput = (id: string, patch: Partial<StoreInput>) =>
    setSaved((s) => ({ ...s, inputs: { ...s.inputs, [id]: { ...(s.inputs[id] ?? EMPTY_INPUT), ...patch } } }));
  const setParam = (key: keyof SimParams, raw: string) => {
    const n = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(n) || n < 0) return;
    setSaved((s) => ({ ...s, params: { ...s.params, [key]: n } }));
  };

  const visibleStores = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("pt-BR");
    return (stores ?? []).filter((s) => !q || s.name.toLocaleLowerCase("pt-BR").includes(q));
  }, [stores, search]);

  const openRoster = (id: string) => {
    setRosterStoreId(id);
    setTab("escala");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const header = (
    <header className="simEsc__header">
      <div className="simEsc__headerInner">
        <img className="simEsc__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
        <div className="simEsc__titles">
          <span className="simEsc__kicker">Simulação</span>
          <h1 className="simEsc__title">Simulador de escala</h1>
          <p className="simEsc__subtitle">
            Informe o volume de pedidos, o quadro de QLP e os freelancers de cada loja para ver a hora/homem, a ação indicada e a
            escala 5x2 do mês. É só uma simulação: nada é gravado.
          </p>
        </div>
      </div>
    </header>
  );

  if (loadError) {
    return (
      <div className="simEsc">
        {header}
        <main className="simEsc__main">
          <div className="ecoCard ecoEmpty" role="alert">
            {loadError}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="simEsc">
      {header}
      <main className="simEsc__main">
        <EcoSegmented<Tab>
          label="Seção do simulador"
          className="simEsc__tabs"
          value={tab}
          onChange={setTab}
          options={[
            { value: "calculadora", label: "Calculadora" },
            { value: "escala", label: "Escala" },
            { value: "parametros", label: "Parâmetros" },
          ]}
        />

        {stores === null ? <p className="simEsc__muted">Carregando lojas...</p> : null}

        {stores && tab === "calculadora" ? (
          <section className="ecoCard simEsc__card">
            <div className="simEsc__toolbar">
              <label className="ecoSearch simEsc__search">
                <span className="ecoVisuallyHidden">Buscar loja</span>
                <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                <input className="ecoInput" placeholder="Buscar loja" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setConfirmClear(true)}>
                Limpar valores
              </button>
            </div>
            <p className="simEsc__hint">
              Preencha volume mensal, QLP, freelancers (opcional), abertura e fechamento. A hora/homem é quantos pedidos cada pessoa
              atende por hora trabalhada.
            </p>
            <div className="simEsc__tableWrap">
              <table className="simEsc__table">
                <thead>
                  <tr>
                    <th>Loja</th>
                    <th>Volume mensal de pedidos</th>
                    <th>QLP</th>
                    <th>Freelancer (opcional)</th>
                    <th>Abertura</th>
                    <th>Fechamento</th>
                    <th>Hora/homem</th>
                    <th>Ação</th>
                    <th>
                      <span className="ecoVisuallyHidden">Escala</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStores.map((s) => {
                    const v = inputOf(s.id);
                    const hh = hourMan(v, params);
                    const action = actionOf(hh, params);
                    return (
                      <tr key={s.id}>
                        <td className="simEsc__store" data-label="Loja">
                          {s.name}
                          {s.state ? <small>{s.state}</small> : null}
                        </td>
                        <td data-label="Volume mensal">
                          <input
                            className="ecoInput simEsc__input"
                            type="number"
                            min={0}
                            inputMode="numeric"
                            aria-label={`Volume mensal de pedidos de ${s.name}`}
                            value={v.volume ?? ""}
                            onChange={(e) => setInput(s.id, { volume: parseCount(e.target.value) })}
                          />
                        </td>
                        <td data-label="QLP">
                          <input
                            className="ecoInput simEsc__input simEsc__input--sm"
                            type="number"
                            min={0}
                            inputMode="numeric"
                            aria-label={`QLP de ${s.name}`}
                            value={v.qlp ?? ""}
                            onChange={(e) => setInput(s.id, { qlp: parseCount(e.target.value) })}
                          />
                        </td>
                        <td data-label="Freelancer">
                          <input
                            className="ecoInput simEsc__input simEsc__input--sm"
                            type="number"
                            min={0}
                            inputMode="numeric"
                            aria-label={`Freelancers de ${s.name}`}
                            value={v.freelancers ?? ""}
                            onChange={(e) => setInput(s.id, { freelancers: parseCount(e.target.value) })}
                          />
                        </td>
                        <td data-label="Abertura">
                          <input
                            className="ecoInput simEsc__input simEsc__input--time"
                            type="time"
                            aria-label={`Abertura de ${s.name}`}
                            value={v.open}
                            onChange={(e) => setInput(s.id, { open: e.target.value })}
                          />
                        </td>
                        <td data-label="Fechamento">
                          <input
                            className="ecoInput simEsc__input simEsc__input--time"
                            type="time"
                            aria-label={`Fechamento de ${s.name}`}
                            value={v.close}
                            onChange={(e) => setInput(s.id, { close: e.target.value })}
                          />
                        </td>
                        <td data-label="Hora/homem" className="simEsc__hh">
                          {hh === null ? <span className="simEsc__muted">—</span> : fmt1(hh)}
                        </td>
                        <td data-label="Ação">
                          {action ? <span className={`simEsc__action simEsc__action--${ACTION_TONE[action]}`}>{ACTION_LABELS[action]}</span> : null}
                        </td>
                        <td className="simEsc__rowAction">
                          <button type="button" className="ecoLinkBtn" onClick={() => openRoster(s.id)}>
                            Ver escala
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!visibleStores.length ? <p className="ecoEmpty">Nenhuma loja encontrada.</p> : null}
            </div>
          </section>
        ) : null}

        {stores && tab === "escala" ? (
          <RosterSection
            stores={stores}
            storeId={rosterStoreId}
            onStore={setRosterStoreId}
            month={rosterMonth}
            onMonth={setRosterMonth}
            months={months}
            input={inputOf(rosterStoreId)}
            params={params}
            onEdit={() => setTab("calculadora")}
          />
        ) : null}

        {tab === "parametros" ? (
          <section className="ecoCard simEsc__card">
            <div className="simEsc__toolbar">
              <h2 className="ecoCard__title">Parâmetros da simulação</h2>
              <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setSaved((s) => ({ ...s, params: DEFAULT_PARAMS }))}>
                Restaurar padrões
              </button>
            </div>
            <div className="simEsc__params">
              {PARAM_FIELDS.map((f) => (
                <div className="ecoField" key={f.key}>
                  <label htmlFor={`param-${f.key}`}>{f.label}</label>
                  <input
                    id={`param-${f.key}`}
                    className="ecoInput"
                    type="number"
                    min={0}
                    step={f.step ?? 1}
                    defaultValue={params[f.key]}
                    key={`${f.key}-${params[f.key]}`}
                    onBlur={(e) => setParam(f.key, e.target.value)}
                  />
                  <span className="simEsc__hint">{f.hint}</span>
                </div>
              ))}
            </div>
            <h3 className="simEsc__subTitle">Como a hora/homem é calculada</h3>
            <ol className="simEsc__how">
              <li>Horas abertas por dia = fechamento − abertura. Fechamento antes da abertura = fecha no dia seguinte (09:00 às 01:30 = 16,5 h).</li>
              <li>Horas de trabalho de um turno = horas abertas ÷ turnos por dia − intervalo.</li>
              <li>Horas trabalhadas no mês = (QLP × dias do mês × dias de trabalho por semana ÷ 7 + freelancers × diárias no mês) × horas de um turno.</li>
              <li>Hora/homem = volume mensal de pedidos ÷ horas trabalhadas no mês.</li>
              <li>
                Ação, pelo valor com uma casa decimal: abaixo de {params.fireBelow} = Demissão; de {params.fireBelow} a menos de{" "}
                {params.freelancerFrom} = Manter; de {params.freelancerFrom} a menos de {params.qlpFrom} = Contratar freelancer;{" "}
                {params.qlpFrom} ou mais = Contratar QLP.
              </li>
              <li>A conta usa a média do mês. Picos de fim de semana podem pedir mais gente do que a média indica.</li>
            </ol>
          </section>
        ) : null}
      </main>

      <EcoConfirmDialog
        open={confirmClear}
        title="Limpar valores"
        message="Apaga os valores digitados em todas as lojas deste navegador. Os parâmetros continuam."
        confirmLabel="Limpar"
        tone="danger"
        onConfirm={() => {
          setSaved((s) => ({ ...s, inputs: {} }));
          setConfirmClear(false);
        }}
        onCancel={() => setConfirmClear(false)}
      />
    </div>
  );
}

type RosterSectionProps = {
  stores: SimulatorStore[];
  storeId: string;
  onStore: (id: string) => void;
  month: string;
  onMonth: (value: string) => void;
  months: Array<{ value: string; label: string }>;
  input: StoreInput;
  params: SimParams;
  onEdit: () => void;
};

function RosterSection({ stores, storeId, onStore, month, onMonth, months, input, params, onEdit }: RosterSectionProps) {
  const [year, monthNumber] = month.split("-").map(Number);
  const qlp = input.qlp ?? 0;
  const roster = useMemo(() => buildRoster(year, monthNumber, qlp), [year, monthNumber, qlp]);
  const hh = hourMan(input, params);
  const action = actionOf(hh, params);
  const store = stores.find((s) => s.id === storeId);

  return (
    <section className="ecoCard simEsc__card">
      <div className="simEsc__rosterFilters">
        <div className="ecoField">
          <label htmlFor="sim-store">Loja</label>
          <select id="sim-store" className="ecoSelect" value={storeId} onChange={(e) => onStore(e.target.value)}>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="ecoField">
          <label htmlFor="sim-month">Mês</label>
          <select id="sim-month" className="ecoSelect" value={month} onChange={(e) => onMonth(e.target.value)}>
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="simEsc__chips">
        <span className="simEsc__chip">
          QLP <strong>{qlp}</strong>
        </span>
        <span className="simEsc__chip">
          Hora/homem <strong>{hh === null ? "—" : fmt1(hh)}</strong>
        </span>
        {action ? <span className={`simEsc__action simEsc__action--${ACTION_TONE[action]}`}>{ACTION_LABELS[action]}</span> : null}
        <button type="button" className="ecoLinkBtn" onClick={onEdit}>
          Editar valores de {store?.name ?? "loja"}
        </button>
      </div>

      {qlp === 0 ? (
        <p className="ecoEmpty">Informe o QLP de {store?.name ?? "loja"} na Calculadora para montar a escala.</p>
      ) : (
        <>
          <p className="simEsc__hint">
            Escala 5x2 do QLP: cada um folga 2 dias por semana (segunda a domingo), nunca encostados. Os freelancers não entram na
            escala; contam só na hora/homem.
          </p>
          <div className="simEsc__gridWrap">
            <table className="simEsc__grid">
              <thead>
                <tr>
                  <th className="simEsc__gridName">Colaborador</th>
                  {roster.days.map((d) => {
                    const weekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                    return (
                      <th key={d.getTime()} className={weekend ? "is-weekend" : undefined}>
                        {WEEKDAY_LETTERS[d.getUTCDay()]}
                        <br />
                        {d.getUTCDate()}
                      </th>
                    );
                  })}
                  <th>Folgas</th>
                  <th>Dias</th>
                </tr>
              </thead>
              <tbody>
                {roster.rows.map((r) => (
                  <React.Fragment key={r.label}>
                    <tr>
                      <td className="simEsc__gridName">
                        {r.label}
                        <small>{r.shift === "MANHA" ? "Manhã" : "Noite"}</small>
                      </td>
                      {r.cells.map((c, di) => (
                        <td key={di} className={c ? `simEsc__cell simEsc__cell--${c}` : "simEsc__cell"}>
                          {c}
                        </td>
                      ))}
                      <td className="simEsc__gridNum">{r.rests}</td>
                      <td className="simEsc__gridNum">{r.workDays}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                {[
                  ["Manhã escalados", roster.morning],
                  ["Noite escalados", roster.night],
                  ["Total escalados", roster.total],
                  ["QLP de folga", roster.qlpOff],
                ].map(([label, values]) => (
                  <tr key={label as string}>
                    <td className="simEsc__gridName">{label as string}</td>
                    {(values as number[]).map((n, di) => (
                      <td key={di} className="simEsc__gridNum">
                        {n}
                      </td>
                    ))}
                    <td colSpan={2} />
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
          <p className="simEsc__legend">
            <span className="simEsc__cell simEsc__cell--M">M</span> manhã <span className="simEsc__cell simEsc__cell--N">N</span> noite{" "}
            <span className="simEsc__cell simEsc__cell--F">F</span> folga · sábado e domingo em destaque no cabeçalho
          </p>
        </>
      )}
    </section>
  );
}
